import { useEffect, useState } from 'react';
import { calculateScrollProgress, getScrollMetrics } from './scrollUtils';

/**
 * Hook that tracks scroll progress for parallax effects
 *
 * @param scrollParent - The scrollable element or window to track
 * @returns A number between 0 and 1 representing scroll progress
 */
export function useScrollProgress(
  scrollParent: HTMLElement | Window | null
): number {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (!scrollParent) {
      return;
    }

    const handleScroll = () => {
      const metrics = getScrollMetrics(scrollParent);
      const progress = calculateScrollProgress(metrics);
      setScrollProgress(progress);
    };

    // Initial calculation
    handleScroll();

    // Listen to scroll events
    scrollParent.addEventListener('scroll', handleScroll, { passive: true });

    // Recalculate on resize (viewport or content size changes)
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(document.documentElement);

    if (scrollParent !== window) {
      resizeObserver.observe(scrollParent as HTMLElement);
    }

    return () => {
      scrollParent.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [scrollParent]);

  return scrollProgress;
}
